import _ from 'lodash'

/**
 * Creating tree in which we save Phrase entities.
 */
export class MachineLearningLabelingService {
  constructor () {
    this.children = []
    this.tokens = []
  }

  static extractTokensFromText (text) {
    const tokens = []
    const re = /(([^\x00-\x7F]|\w)+|\S)\s*/giu // eslint-disable-line no-control-regex
    let res

    while ((res = re.exec(text)) !== null) {
      const match = res[0]
      const spaced = match !== match.trim()

      tokens.push({
        spaced,
        value: match.trim(),
        start: res.index,
        end: (spaced ? (re.lastIndex - 1) : re.lastIndex) - 1
      })
    }
    return tokens
  }

  updateTokens (tokens) {
    this.tokens = tokens
    this.reset()
  }

  reset () {
    this.children = []
  }

  addEntity (newEntity) {
    newEntity = this._addTokenPosition(newEntity)
    const headNode = this._addEntityToNodeChildren({ children: this.children }, newEntity)
    this.children = headNode.children
  }

  removeEntity (entity) {
    this.children = this._removeEntity(this.children, entity)
  }

  getEntities () {
    const removeToken = (entity) => {
      const { start, end, ...rest } = entity
      return {
        ...rest,
        children: (rest.children || []).map((child) => removeToken(child))
      }
    }

    return (this.children || []).map((child) => removeToken(child))
  }

  _removeEntity (children, entity) {
    // Removing top level entities
    const filteredChildren = children.filter((child) => child.entity !== entity.entity || child.start !== entity.start || child.end !== entity.end)

    if (filteredChildren.length !== children.length) {
      return filteredChildren
    }
    return children.map((child) => ({
      ...child,
      children: this._removeEntity(child.children || [], entity)
    }))
  }

  _getTokenPosition (start, end) {
    return {
      start: this.tokens.findIndex((token) => token.start === start),
      end: this.tokens.findIndex((token) => token.end === end)
    }
  }

  _addTokenPosition (entity) {
    const { start, end } = this._getTokenPosition(entity.startPos, entity.endPos)
    return {
      ...entity,
      children: (entity.children || []).map((child) => this._addTokenPosition(child)),
      start,
      end
    }
  }

  _addEntityToNodeChildren (node, newEntity) {
    let hasUpdated = false
    node.children = _.uniq((node.children || []).reduce((acc, child) => {
      if (!this._shouldUpdateEntity(child, newEntity)) {
        acc.push(child)
        return acc
      }
      hasUpdated = true
      // can be an array for cannibalized nodes
      const updatedNode = this._addEntityToNode(child, newEntity)
      if (Array.isArray(updatedNode)) {
        acc.push(...updatedNode)
      } else {
        acc.push(updatedNode)
      }
      return acc
    }, []))

    if (!hasUpdated) {
      node.children.push(newEntity)
    }

    return node
  }

  _addEntityToNode (node, newEntity) {
    if (!this._shouldUpdateEntity(node, newEntity)) {
      return node
    }

    // New entity simply overlaps old one
    if (node.startPos >= newEntity.startPos && node.endPos <= newEntity.endPos) {
      return newEntity
    }

    if (node.startPos <= newEntity.startPos && node.endPos >= newEntity.endPos) {
      return this._insertIntoNode(node, newEntity)
    }

    return this._cannibalizeNode(node, newEntity)
  }

  // insert new entity inside old one
  _insertIntoNode (node, newEntity) {
    // this should never happen, because it should be not possible to insert entity in itself
    if (!newEntity.children || newEntity.children.length === 0) {
      const error = new Error(`Could not update entity "${node.entity}" has no children`)
      error.customFields = { node, newEntity }
      throw error
    }

    return this._addEntityToNodeChildren(node, newEntity.children[0])
  }

  _cannibalizeNode (node, newEntity) {
    const newStart = node.start < newEntity.start ? node.start : newEntity.end + 1
    const newEnd = node.end > newEntity.end ? node.end : newEntity.start - 1

    return [this._updateNodePosition(node, newStart, newEnd), newEntity]
  }

  _updateNodePosition (node, start, end) {
    if (node.end < start || node.start > end) {
      return null
    }

    const startPos = this.tokens[start].start
    const endPos = this.tokens[end].end

    return {
      ...node,
      start: node.start > start ? node.start : start,
      end: node.end < end ? node.end : end,
      startPos: node.start > start ? node.startPos : startPos,
      endPos: node.end < end ? node.end : endPos,
      children: node.children
        .map((child) => this._updateNodePosition(child, start, end))
        .filter((child) => !child)
    }
  }

  _shouldUpdateEntity (oldEntity, newEntity) {
    if (oldEntity.entity !== newEntity.entity) {
      return false
    }

    // (left | right | middle)
    return (oldEntity.start <= newEntity.end && oldEntity.start >= newEntity.start) ||
            (oldEntity.end <= newEntity.end && oldEntity.end >= newEntity.start) ||
            (oldEntity.start <= newEntity.start && oldEntity.end >= newEntity.end)
  }
}
